import React, { useEffect, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useAlert } from "components/UI_components/Alert";
import EllipsisMenu from "components/UI_components/EllipsisMenu";

import {
  ArrowLeft,
  UserPlus,
  CheckCircle2,
  RotateCcw,
  Download,
  Upload,
  MapPin,
  Send,
  PlusCircle,
  UserCheck,
  AlertTriangle,
  XCircle,
  FileText,
  Clock3,
  Trash2,
  Tag,
  User,
  Calendar,
  ImageIcon,
  File,
  Loader2,
} from "lucide-react";

import ImageZoomModal from "../../screens/Dashboard/Design/components/ImageZoomModal";

/* ------------------ utils ------------------ */
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/* ------------------ Button ------------------ */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition",
  {
    variants: {
      variant: {
        default: "bg-sm-primary text-white hover:bg-sm-primary/90",
        destructive: "bg-red-600 text-white",
        outline: "border hover:bg-sm-accent hover:text-white",
        ghost: "hover:bg-gray-100",
      },
      size: {
        default: "h-10 px-4",
        sm: "h-9 px-3",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);

/* ------------------ Badge ------------------ */
function Badge({ className, children, ...props }) {
  return (
    <div
      className={cn("px-2 py-1 text-xs rounded bg-gray-200", className)}
      {...props}
    >
      {children}
    </div>
  );
}

/* ------------------ Textarea ------------------ */
const Textarea = React.forwardRef(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      "flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm",
      className,
    )}
    {...props}
  />
));

/* ------------------ CONFIG ------------------ */

const statusColors = {
  Draft: "bg-gray-bg text-text-gray border border-sm-border",
  Open: "bg-sm-card text-text-clr border border-sm-border",
  "Pending Approval": "bg-[#FEF3C7] text-[#B4530A] border border-sm-border",
  "Issue Raised": "bg-btn-bg-2/15 text-text-clr border border-sm-border",
  "Not Planned": "bg-gray-bg text-text-gray border border-sm-border",
  Resolved: "bg-light-green text-dark-green border border-transparent",
  Closed: "bg-light-green text-dark-green border border-transparent",
  Rejected: "bg-dark-red/10 text-dark-red border border-transparent",
};

const priorityColors = {
  Critical: "bg-dark-red/10 text-dark-red border border-transparent",
  High: "bg-btn-bg-2/15 text-text-clr border border-transparent",
  Medium: "bg-task-hover text-text-clr border border-transparent",
  Low: "bg-gray-bg text-text-gray border border-sm-border",
};

const activityIconMap = {
  created: PlusCircle,
  draft: FileText,
  assigned: UserCheck,
  accepted: CheckCircle2,
  committed: Clock3,
  attachment: Upload,
  issue_raised: AlertTriangle,
  approved: CheckCircle2,
  rejected: XCircle,
  closed: CheckCircle2,
  comment: Send,
  reopened: RotateCcw,
};

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

const PRIORITY_NUM_TO_LABEL = {
  0: "Critical",
  1: "High",
  2: "Medium",
  3: "Low",
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizePriority = (priority, fallback = "Medium") => {
  if (priority === null || priority === undefined || priority === "") {
    return fallback;
  }
  if (typeof priority === "number") {
    return PRIORITY_NUM_TO_LABEL[priority] || fallback;
  }
  const normalized = toTitleCase(priority);
  return normalized || fallback;
};

const normalizeStatus = (status, fallback = "Open") => {
  if (!status) return fallback;
  const normalized = toTitleCase(status);
  if (normalized === "In Progress") return "Open";
  return normalized || fallback;
};

const normalizeAttachments = (detail) => {
  if (Array.isArray(detail?.attachments)) return detail.attachments;
  if (Array.isArray(detail?.snag_attachments)) return detail.snag_attachments;
  return [];
};

const normalizeActivities = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.results)
        ? payload.results
        : [];

  return list.map((a, index) => ({
    id: String(a?.id ?? a?.activity_id ?? `act-${index + 1}`),
    type: a?.event_type || a?.type || a?.action || "comment",
    user:
      a?.created_by ||
      a?.user_name ||
      a?.user?.full_name ||
      a?.user?.name ||
      a?.performed_by_name ||
      "User",
    timestamp: a?.created_on || a?.created_at || a?.timestamp || "",
    text:
      a?.event_remarks ||
      a?.description ||
      a?.message ||
      a?.text ||
      a?.action ||
      "Activity updated",
    attachment: pickMediaUrl(a?.related_attachment || a?.attachment || a),
  }));
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const toAbsoluteMediaUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (/^https?:\/\//i.test(url) || /^blob:/i.test(url)) return url;
  if (!API_BASE_URL) return url;
  if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/${url}`;
};

const pickMediaUrl = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") return toAbsoluteMediaUrl(entry);

  const candidates = [
    entry?.url,
    entry?.file,
    entry?.image,
    entry?.image_url,
    entry?.attachment,
    entry?.attachment_url,
    entry?.resolved_image,
    entry?.resolution_image,
    entry?.after_image,
  ];

  const match = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  return match ? toAbsoluteMediaUrl(match) : "";
};

const isImageFile = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|svg|webp)($|\?)/i.test(url);
};

const normalizeResolvedImage = (detail, baseSnag = {}) => {
  const directCandidates = [
    detail?.resolved_image,
    detail?.resolution_image,
    detail?.after_image,
    detail?.fixed_image,
    detail?.proof_image,
    detail?.approval_image,
    detail?.issue_after_image,
    detail?.resolved_issue_image,
    baseSnag?.resolvedImage,
    baseSnag?.resolved_image,
  ];

  for (const candidate of directCandidates) {
    const url = pickMediaUrl(candidate);
    if (url) return url;
  }

  const attachmentGroups = [
    ...toArray(detail?.attachments),
    ...toArray(detail?.snag_attachments),
    ...toArray(detail?.resolved_attachments),
  ];
  const resolvedAttachment = attachmentGroups.find((item) =>
    /resolve|approval|fix|after/i.test(String(item?.status || item?.type || "")),
  );
  const anyAttachment = attachmentGroups.find((item) => pickMediaUrl(item));

  const activityAttachments = toArray(detail?.activities)
    .flatMap((activity) => toArray(activity?.attachments))
    .filter(Boolean);
  const resolvedActivityAttachment = activityAttachments.find((item) =>
    /resolve|approval|fix|after/i.test(String(item?.status || item?.type || "")),
  );
  const anyActivityAttachment = activityAttachments.find((item) => pickMediaUrl(item));

  return (
    pickMediaUrl(resolvedAttachment) ||
    pickMediaUrl(anyAttachment) ||
    pickMediaUrl(resolvedActivityAttachment) ||
    pickMediaUrl(anyActivityAttachment) ||
    ""
  );
};

const ADMIN_ACTIONS_BY_STATUS = {
  Draft: ["assign", "resolve", "notPlanned", "reopen"],
  Open: ["assign", "resolve", "notPlanned", "reopen"],
  "Pending Approval": ["assign", "resolve", "notPlanned", "reopen"],
  "Issue Raised": ["assign", "resolve", "notPlanned", "reopen"],
  Resolved: ["assign", "resolve", "notPlanned", "reopen"],
  Closed: ["assign", "resolve", "notPlanned", "reopen"],
  Rejected: ["assign", "resolve", "notPlanned", "reopen"],
  "Not Planned": ["assign", "resolve", "notPlanned", "reopen"],
};

const ASSIGNEE_UPLOADABLE_STATUSES = new Set(["Open", "Issue Raised"]);

const ASSIGNEE_RESOLVED_STATE_MESSAGE = {
  "Pending Approval": "Resolution submitted - awaiting approval",
  Resolved: "Issue resolved",
  Closed: "Issue resolved",
};

const mergeSnagDetail = (baseSnag, detail, activities) => ({
  ...baseSnag,
  id: String(detail?.id ?? baseSnag?.id ?? ""),
  code:
    detail?.code ||
    detail?.snag_code ||
    baseSnag?.code ||
    `SNG-${String(detail?.id || baseSnag?.id || "").padStart(3, "0")}`,
  title: detail?.title || baseSnag?.title || "Snag",
  description: detail?.description ?? baseSnag?.description ?? "",
  project:
    detail?.project_name ||
    detail?.project?.name ||
    detail?.site_name ||
    detail?.site?.site_name ||
    baseSnag?.project ||
    "Unassigned",
  location: detail?.location || baseSnag?.location || "N/A",
  category: detail?.category || baseSnag?.category || "Other",
  subcategory: detail?.subcategory || baseSnag?.subcategory || "",
  siteId: detail?.site || detail?.site_id || detail?.site?.id || baseSnag?.siteId || null,
  priority: normalizePriority(detail?.priority, baseSnag?.priority || "Medium"),
  status: normalizeStatus(detail?.status, baseSnag?.status || "Open"),
  expectedClosure:
    detail?.expected_closure_date?.slice?.(0, 10) ||
    baseSnag?.expectedClosure ||
    "",
  image: detail?.issue_image || baseSnag?.image || "/placeholder.svg",
  resolvedImage: normalizeResolvedImage(detail, baseSnag),
  attachments: normalizeAttachments(detail),
  activities: activities || baseSnag?.activities || [],
});

/* ------------------ MAIN ------------------ */

export function SnagDetailView({
  snag,
  onBack,
  viewMode,
  isAdmin,
  organizationId,
  token: tokenProp,
  onSnagUpdated,
  onSnagDeleted,
}) {
  const { addAlert } = useAlert();
  const [comment, setComment] = useState("");
  const [resolvedRemarks, setResolvedRemarks] = useState("");
  const [detailSnag, setDetailSnag] = useState(snag);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [updatingSnag, setUpdatingSnag] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingSnag, setDeletingSnag] = useState(false);
  const [resolvedImage, setResolvedImage] = useState(null);
  const [resolvedImagePreviewUrl, setResolvedImagePreviewUrl] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const [assigningMembers, setAssigningMembers] = useState(false);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [assignExpectedClosure, setAssignExpectedClosure] = useState("");
  const [zoomModal, setZoomModal] = useState({
    isOpen: false,
    imageUrl: "",
    itemName: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  const openZoom = (url, name = "Image Detail") => {
    if (!url || url === "/placeholder.svg") return;
    setZoomModal({ isOpen: true, imageUrl: url, itemName: name });
  };
  const token = tokenProp || localStorage.getItem("access");

  useEffect(() => {
    setDetailSnag(snag);
  }, [snag]);

  const fetchDetail = async () => {
    if (!organizationId || !token || !snag?.id || !API_BASE_URL) return;

    setLoadingDetail(true);
    setDetailError("");
    try {
      const detailEndpoints = [
        `/snag-management/org/${organizationId}/snags/${snag.id}/`,
        `/snag-management/org/${organizationId}/snags/get-a-snag/${snag.id}/`,
      ];

      let detailPayload = null;
      let lastDetailErr = "";

      for (const path of detailEndpoints) {
        const res = await fetch(`${API_BASE_URL}${path}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          detailPayload = data;
          break;
        }
        lastDetailErr = data?.detail || data?.message || `${res.status}`;
      }

      if (!detailPayload) {
        throw new Error(lastDetailErr || "Failed to fetch snag details");
      }

      const activityRes = await fetch(
        `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${snag.id}/activities/`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const activityPayload = await activityRes.json().catch(() => ({}));
      const activities = activityRes.ok
        ? normalizeActivities(activityPayload)
        : snag?.activities || [];

      const normalizedDetail = mergeSnagDetail(
        snag,
        {
          ...(detailPayload?.data || detailPayload),
          activities,
        },
        activities,
      );
      setDetailSnag(normalizedDetail);
    } catch (e) {
      setDetailError(e.message || "Failed to fetch snag details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchAttachments = async () => {
    if (!organizationId || !token || !snag?.id || !API_BASE_URL) return;
    setLoadingAttachments(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${snag.id}/attachments/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setAttachments(Array.isArray(data) ? data : data?.results || []);
      }
    } catch (e) {
      console.error("Failed to fetch attachments", e);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleUploadAttachments = async (files) => {
    if (!files || files.length === 0 || !organizationId || !token || !snag?.id) return;

    setUploadingAttachments(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("file", files[i]);
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${snag.id}/attachments/upload/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      if (res.ok) {
        addAlert("Attachments uploaded successfully", "success");
        fetchAttachments();
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || data?.message || "Upload failed");
      }
    } catch (e) {
      addAlert(e.message, "error");
    } finally {
      setUploadingAttachments(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!organizationId || !token || !snag?.id || !attachmentId) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${snag.id}/attachments/${attachmentId}/delete/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        addAlert("Attachment deleted", "success");
        fetchAttachments();
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || data?.message || "Delete failed");
      }
    } catch (e) {
      addAlert(e.message, "error");
    }
  };

  useEffect(() => {
    fetchDetail();
    fetchAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, token, snag?.id]);

  useEffect(() => {
    if (!resolvedImage) {
      setResolvedImagePreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(resolvedImage);
    setResolvedImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [resolvedImage]);

  const currentSnag = detailSnag || snag;
  const adminActions =
    ADMIN_ACTIONS_BY_STATUS[currentSnag?.status] ||
    ADMIN_ACTIONS_BY_STATUS.Open;
  const isPendingApproval = currentSnag?.status === "Pending Approval";
  const showAdminReviewLayout =
    isAdmin &&
    (["Pending Approval", "Issue Raised", "Resolved", "Closed"].includes(
      currentSnag?.status,
    ) || currentSnag?.resolvedImage);

  const canUploadResolution =
    !isAdmin &&
    ASSIGNEE_UPLOADABLE_STATUSES.has(currentSnag?.status);

  const loadAssignMembers = async () => {
    if (!organizationId || !token || !currentSnag?.id || !API_BASE_URL) return;

    setMembersLoading(true);
    setMembersError("");
    setAssignExpectedClosure(currentSnag?.expectedClosure || "");
    try {
      const [orgMembersRes, assignedRes] = await Promise.all([
        fetch(`${API_BASE_URL}/org/organization/${organizationId}/members/`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(
          `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${currentSnag.id}/assign/`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      const orgMembersPayload = await orgMembersRes.json().catch(() => ({}));
      const assignedPayload = await assignedRes.json().catch(() => ({}));

      if (!orgMembersRes.ok) {
        throw new Error(
          orgMembersPayload?.detail ||
          orgMembersPayload?.message ||
          `Failed to fetch members (${orgMembersRes.status})`,
        );
      }

      const orgList = Array.isArray(orgMembersPayload)
        ? orgMembersPayload
        : Array.isArray(orgMembersPayload?.data)
          ? orgMembersPayload.data
          : Array.isArray(orgMembersPayload?.results)
            ? orgMembersPayload.results
            : [];

      const members = orgList.map((m, index) => ({
        id: String(m?.id),
        label:
          `${m?.user?.first_name || ""} ${m?.user?.last_name || ""}`.trim() ||
          `Member ${index + 1}`,
      }));

      const assignedList = Array.isArray(assignedPayload)
        ? assignedPayload
        : Array.isArray(assignedPayload?.data)
          ? assignedPayload.data
          : Array.isArray(assignedPayload?.results)
            ? assignedPayload.results
            : [];

      const assignedIds = assignedList
        .map((m) =>
          String(m?.assignee ?? ""),
        )
        .filter(Boolean);
      const assignedNames = assignedList
        .map((m) => m?.assignee_name)
        .filter(Boolean);

      setOrganizationMembers(members);
      if (assignedIds.length > 0) {
        setSelectedMemberIds(assignedIds);
      } else if (assignedNames.length > 0) {
        const lower = new Set(assignedNames.map((name) => name.toLowerCase()));
        setSelectedMemberIds(
          members
            .filter((m) => lower.has(String(m.label).toLowerCase()))
            .map((m) => m.id),
        );
      } else {
        setSelectedMemberIds([]);
      }

      if (assignedNames.length > 0) {
        const updated = { ...currentSnag, assignedTo: assignedNames };
        setDetailSnag(updated);
        onSnagUpdated?.(updated);
      }
    } catch (e) {
      setMembersError(e.message || "Failed to load assign members");
      setOrganizationMembers([]);
      setSelectedMemberIds([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const toggleMember = (memberId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId],
    );
  };

  const assignMembers = async () => {
    if (!organizationId || !token || !currentSnag?.id || !API_BASE_URL) return;

    setAssigningMembers(true);
    setMembersError("");
    try {
      const numericIds = selectedMemberIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      const payloadIds =
        numericIds.length === selectedMemberIds.length
          ? numericIds
          : selectedMemberIds;

      const url = `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${currentSnag.id}/assign/`;
      const methods = ["POST"];
      let assigned = false;
      let lastError = "";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ member_ids: payloadIds }),
      });

      if (res.ok) {
        assigned = true;
      }

      const payload = await res.json().catch(() => ({}));
      lastError =
        payload?.detail ||
        payload?.message ||
        Object.values(payload || {})?.[0]?.[0] ||
        `Assign failed (${res.status})`;

      if (!assigned) {
        throw new Error(lastError || "Failed to assign members");
      }

      const shouldOpenDraft =
        currentSnag?.status === "Draft" &&
        payloadIds.length > 0 &&
        Boolean(assignExpectedClosure);

      const updatePayload = {};
      if (assignExpectedClosure) {
        updatePayload.expected_closure_date = assignExpectedClosure;
      }
      if (shouldOpenDraft) {
        updatePayload.status = "Open";
      }

      if (Object.keys(updatePayload).length > 0) {
        const endpoints = [
          `/snag-management/org/${organizationId}/snags/${currentSnag.id}/`,
          `/snag-management/org/${organizationId}/snags/${currentSnag.id}/update/`,
        ];
        let updatedMeta = false;
        let updateError = "";

        for (const endpoint of endpoints) {
          for (const method of ["PATCH", "PUT"]) {
            const res = await fetch(`${API_BASE_URL}${endpoint}`, {
              method,
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(updatePayload),
            });
            if (res.ok) {
              updatedMeta = true;
              break;
            }
            const payload = await res.json().catch(() => ({}));
            updateError =
              payload?.detail ||
              payload?.message ||
              `Update failed (${res.status})`;
          }
          if (updatedMeta) break;
        }

        if (!updatedMeta && shouldOpenDraft) {
          throw new Error(updateError || "Failed to move draft to open");
        }
      }

      const assignedNames = organizationMembers
        .filter((m) => selectedMemberIds.includes(String(m.id)))
        .map((m) => m.label);
      const updated = {
        ...currentSnag,
        assignedTo: assignedNames,
        expectedClosure: assignExpectedClosure || currentSnag?.expectedClosure,
        status:
          currentSnag?.status === "Draft" &&
            payloadIds.length > 0 &&
            Boolean(assignExpectedClosure)
            ? "Open"
            : currentSnag?.status,
      };
      setDetailSnag(updated);
      onSnagUpdated?.(updated);
      await fetchDetail();
      setAssignOpen(false);
    } catch (e) {
      setMembersError(e.message || "Failed to assign members");
    } finally {
      setAssigningMembers(false);
    }
  };

  const postSnagAction = async ({
    endpoint,
    commentText,
    imageFile,
    fallbackStatus,
  }) => {
    if (!organizationId || !token || !currentSnag?.id || !API_BASE_URL) return;

    setUpdatingSnag(true);
    setDetailError("");
    try {
      const endpoints = Array.isArray(endpoint) ? endpoint : [endpoint];
      const hasFile = !!imageFile;
      let lastError = "Action failed";
      let success = false;

      for (const actionPath of endpoints) {
        const normalizedComment = String(commentText || "").trim();
        const body = hasFile
          ? new FormData()
          : JSON.stringify({
            comment: normalizedComment,
          });
        if (hasFile) {
          if (normalizedComment) body.append("comment", normalizedComment);
          body.append("image", imageFile);
        }

        const res = await fetch(
          `${API_BASE_URL}/snag-management/org/${organizationId}/snags/${currentSnag.id}/${actionPath}/`,
          {
            method: "POST",
            headers: hasFile
              ? { Authorization: `Bearer ${token}` }
              : {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            body,
          },
        );
        const payload = await res.json().catch(() => ({}));
        if (res.ok) {
          success = true;
          break;
        }
        lastError =
          payload?.detail || payload?.message || `Action failed (${res.status})`;
      }

      if (!success) {
        throw new Error(lastError);
      }

      const updated = mergeSnagDetail(
        currentSnag,
        { ...currentSnag, status: fallbackStatus || currentSnag?.status },
        currentSnag?.activities || [],
      );
      setDetailSnag(updated);
      onSnagUpdated?.(updated);
      await fetchDetail();
    } catch (e) {
      setDetailError(e.message || "Failed to perform action");
    } finally {
      setUpdatingSnag(false);
    }
  };

  const deleteSnag = async () => {
    if (!organizationId || !token || !currentSnag?.id || !API_BASE_URL) return;

    setDeletingSnag(true);
    setDetailError("");
    try {
      const endpoints = [
        `/snag-management/org/${organizationId}/snags/${currentSnag.id}/`,
        `/snag-management/org/${organizationId}/snags/${currentSnag.id}/delete/`,
      ];
      let deleted = false;
      let lastError = "";

      for (const endpoint of endpoints) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          deleted = true;
          break;
        }
        const payload = await res.json().catch(() => ({}));
        lastError =
          payload?.detail || payload?.message || `Delete failed (${res.status})`;
      }

      if (!deleted) throw new Error(lastError || "Delete failed");
      addAlert("Snag deleted successfully", "success");
      onSnagDeleted?.(currentSnag.id);
      onBack?.();
    } catch (e) {
      addAlert(e.message || "Failed to delete snag", "error");
      setDetailError(e.message || "Failed to delete snag");
    } finally {
      setDeletingSnag(false);
    }
  };

  const handleAdminResolve = () => {
    postSnagAction({
      endpoint: "resolve",
      commentText: comment,
      fallbackStatus: "Resolved",
    });
  };

  const handleAdminRaiseIssue = () => {
    if (isPendingApproval && !comment.trim()) {
      setDetailError("Comment is required to raise issue.");
      return;
    }

    postSnagAction({
      endpoint: ["raise-issue"],
      commentText: comment,
      fallbackStatus: "Issue Raised",
    });
  };

  const handleAdminNotPlanned = () => {
    postSnagAction({
      endpoint: ["not-planned"],
      commentText: comment,
      fallbackStatus: "Not Planned",
    });
  };

  const submitAdminComment = async () => {
    if (!organizationId || !token || !currentSnag?.id || !API_BASE_URL) return;
    const trimmedComment = String(comment || "").trim();
    if (!trimmedComment) {
      setDetailError("Comment cannot be empty.");
      return;
    }

    setSubmittingComment(true);
    setDetailError("");
    try {
      const basePath = `/snag-management/org/${organizationId}/snags/${currentSnag.id}`;
      const commentPayload = {
        comment: trimmedComment
      };
      const attempts = [
        { path: `${basePath}/add/comment/`, method: "POST" },
      ];

      let posted = false;
      let lastError = "";

      for (const attempt of attempts) {
        const res = await fetch(`${API_BASE_URL}${attempt.path}`, {
          method: attempt.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(commentPayload),
        });
        const payload = await res.json().catch(() => ({}));
        if (res.ok) {
          posted = true;
          break;
        }
        lastError =
          payload?.detail || payload?.message || `Comment failed (${res.status})`;
      }

      if (!posted) {
        throw new Error(lastError || "Failed to add comment");
      }

      setComment("");
      addAlert("Comment added", "success");
      await fetchDetail();
    } catch (e) {
      setDetailError(e.message || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDownload = (fileUrl) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileUrl.split("/").pop();
    link.click();
  };

  return (
    <div className="flex flex-col gap-3 h-full pt-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9 hover:bg-sm-accent hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-gray">{currentSnag?.code}</span>
              <Badge className={`${statusColors[currentSnag?.status] || ""} text-xs`}>
                {currentSnag?.status}
              </Badge>
              <Badge
                className={`${priorityColors[currentSnag?.priority] || ""} text-xs`}
              >
                {currentSnag?.priority}
              </Badge>
            </div>

            <h2 className="mt-1 text-xl font-semibold text-primary-text">
              {currentSnag?.title}
            </h2>

            <div className="mt-1 flex items-center gap-1 text-sm text-text-gray">
              <MapPin className="h-4 w-4" />
              {currentSnag?.project} - {currentSnag?.location}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {isAdmin && (
            <>
              {adminActions.includes("assign") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-sm-border text-sm"
                  onClick={() =>
                    setAssignOpen((prev) => {
                      const next = !prev;
                      if (next) loadAssignMembers();
                      return next;
                    })
                  }
                >
                  <UserPlus className="mr-1 h-4 w-4" />
                  Assign
                </Button>
              )}
              {adminActions.includes("resolve") && (currentSnag.status !== "Resolved" && currentSnag.status !== "Not Planned") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-sm-border text-sm"
                  onClick={handleAdminResolve}
                  disabled={updatingSnag}
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  {updatingSnag ? "Updating..." : "Resolve"}
                </Button>
              )}
              {adminActions.includes("notPlanned") && currentSnag.status !== "Not Planned" && currentSnag.status !== "Resolved" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-sm-border text-sm"
                  onClick={handleAdminNotPlanned}
                  disabled={updatingSnag}
                >
                  <XCircle className="mr-1 h-4 w-4" />
                  {updatingSnag ? "Updating..." : "Not Planned"}
                </Button>
              )}
              {adminActions.includes("reopen") && (currentSnag.status == "Resolved" || currentSnag.status == "Not Planned") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 border-sm-border text-sm"
                  onClick={() =>
                    postSnagAction({
                      endpoint: ["open", "reopen"],
                      commentText: comment,
                      fallbackStatus: "Open",
                    })
                  }
                  disabled={updatingSnag}
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Reopen
                </Button>
              )}
            </>
          )}

          {/* <Button
            size="sm"
            variant="outline"
            className="h-9 w-9 border-sm-border p-0"
            onClick={() => handleDownload(currentSnag?.file)}
          >
            <Download className="h-4 w-4" />
          </Button> */}
          {isAdmin && (
            <EllipsisMenu
              items={[
                {
                  key: "delete-snag",
                  label: deletingSnag ? "Deleting..." : "Delete",
                  icon: Trash2,
                  danger: true,
                  disabled: deletingSnag,
                  onClick: () =>
                    addAlert("Delete this snag? This action cannot be undone.", "confirm", {
                      onConfirm: deleteSnag,
                    }),
                },
              ]}
              buttonAriaLabel="Snag actions"
            />
          )}
        </div>
      </div>
      {loadingDetail && (
        <div className="rounded-lg border border-sm-border bg-sm-card px-3 py-2 text-sm text-text-gray">
          Loading snag details...
        </div>
      )}
      {detailError && (
        <div className="rounded-lg border border-dark-red/20 bg-dark-red/10 px-3 py-2 text-sm text-dark-red">
          {detailError}
        </div>
      )}
      {isAdmin && assignOpen && (
        <div className="rounded-xl border border-sm-border bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-primary-text">
              Assign Members
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => setAssignOpen(false)}
            >
              Close
            </Button>
          </div>

          {membersLoading ? (
            <p className="mt-3 text-sm text-text-gray">Loading members...</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {organizationMembers.map((member) => {
                const active = selectedMemberIds.includes(member.id);
                return (
                  <Badge
                    key={member.id}
                    className={`cursor-pointer border ${active
                      ? "border-primary bg-sm-primary text-white"
                      : "border-sm-border bg-background text-text-clr"
                      }`}
                    onClick={() => toggleMember(member.id)}
                  >
                    {member.label}
                  </Badge>
                );
              })}
              {!membersLoading && organizationMembers.length === 0 && (
                <p className="text-sm text-text-gray">No members available.</p>
              )}
            </div>
          )}

          {membersError && (
            <p className="mt-3 text-sm text-dark-red">{membersError}</p>
          )}

          <div className="mt-4">
            <label className="text-xs font-semibold uppercase tracking-wide text-text-gray">
              Expected Closure Date
            </label>
            <input
              type="date"
              value={assignExpectedClosure}
              onChange={(e) => setAssignExpectedClosure(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-sm-border bg-background px-3 text-sm text-primary-text"
            />
            {currentSnag?.status === "Draft" && (
              <p className="mt-1 text-xs text-text-gray">
                Draft will move to Open when assignees and closure date are saved.
              </p>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              className="h-9 bg-sm-primary text-white"
              onClick={assignMembers}
              disabled={
                assigningMembers ||
                membersLoading ||
                (currentSnag?.status === "Draft" &&
                  (!assignExpectedClosure || selectedMemberIds.length === 0))
              }
            >
              {assigningMembers
                ? "Saving..."
                : currentSnag?.status === "Draft"
                  ? "Save & Move to Open"
                  : "Save Assignment"}
            </Button>
          </div>
        </div>
      )}

      {!isAdmin ? (
        <AssigneeLayout
          snag={currentSnag}
          canUpload={canUploadResolution}
          resolvedRemarks={resolvedRemarks}
          setResolvedRemarks={setResolvedRemarks}
          onSubmitResolution={() =>
            postSnagAction({
              endpoint: "send-for-approval",
              commentText: resolvedRemarks,
              imageFile: resolvedImage,
              fallbackStatus: "Pending Approval",
            })
          }
          onImageChange={(file) => setResolvedImage(file)}
          onClearImage={() => setResolvedImage(null)}
          resolvedImageName={resolvedImage?.name || ""}
          resolvedImageUrl={resolvedImagePreviewUrl || currentSnag?.resolvedImage || ""}
          updatingSnag={updatingSnag}
          onImageClick={openZoom}
          attachments={attachments}
          loadingAttachments={loadingAttachments}
          uploadingAttachments={uploadingAttachments}
          onUploadAttachments={handleUploadAttachments}
          onDeleteAttachment={handleDeleteAttachment}
          isAdmin={isAdmin}
        />
      ) : showAdminReviewLayout ? (
        <AssigneeLayout
          snag={currentSnag}
          canUpload={isPendingApproval || currentSnag.status === "Open"}
          resolvedRemarks={comment}
          setResolvedRemarks={setComment}
          onSubmitResolution={handleAdminRaiseIssue}
          onImageChange={null}
          onClearImage={null}
          resolvedImageName=""
          resolvedImageUrl={currentSnag?.resolvedImage || ""}
          updatingSnag={updatingSnag}
          submitLabel="Raise Issue"
          onImageClick={openZoom}
          attachments={attachments}
          loadingAttachments={loadingAttachments}
          uploadingAttachments={uploadingAttachments}
          onUploadAttachments={handleUploadAttachments}
          onDeleteAttachment={handleDeleteAttachment}
          isAdmin={isAdmin}
        />
      ) : (
        <AdminLayout
          snag={currentSnag}
          comment={comment}
          setComment={setComment}
          onSubmitComment={submitAdminComment}
          submittingComment={submittingComment}
          onImageClick={openZoom}
          attachments={attachments}
          loadingAttachments={loadingAttachments}
          uploadingAttachments={uploadingAttachments}
          onUploadAttachments={handleUploadAttachments}
          onDeleteAttachment={handleDeleteAttachment}
          isAdmin={isAdmin}
        />
      )}

      <ImageZoomModal
        isOpen={zoomModal.isOpen}
        onClose={() => setZoomModal((prev) => ({ ...prev, isOpen: false }))}
        imageUrl={zoomModal.imageUrl}
        itemName={zoomModal.itemName}
      />
    </div>
  );
}

/* ------------------ ADMIN ------------------ */

function AdminLayout({
  snag,
  comment,
  setComment,
  onSubmitComment,
  submittingComment,
  onImageClick,
  attachments,
  loadingAttachments,
  uploadingAttachments,
  onUploadAttachments,
  onDeleteAttachment,
  isAdmin,
}) {
  return (
    <div className="overflow-y-auto pr-1">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <div
            className="aspect-video cursor-zoom-in overflow-hidden rounded-xl border border-sm-border bg-gray-bg"
            onClick={() => onImageClick?.(snag.image, snag.code)}
          >
            <img
              src={snag.image || "/placeholder.svg"}
              alt="Snag"
              className="h-full w-full object-cover"
            />
          </div>
          <IssueDetailsCard snag={snag} />
          <AttachmentsCard
            attachments={attachments}
            loading={loadingAttachments}
            uploading={uploadingAttachments}
            onUpload={onUploadAttachments}
            onDelete={onDeleteAttachment}
            onImageClick={onImageClick}
            isAdmin={isAdmin}
          />
        </div>

        <div className="space-y-4">
          <ActivityTimeline activities={snag.activities} onImageClick={onImageClick} />
          <CommentInput
            comment={comment}
            setComment={setComment}
            onSubmit={onSubmitComment}
            submitting={submittingComment}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------ ASSIGNEE ------------------ */

function AssigneeLayout({
  snag,
  canUpload,
  resolvedRemarks,
  setResolvedRemarks,
  onSubmitResolution,
  onImageChange,
  onClearImage,
  resolvedImageName,
  resolvedImageUrl,
  updatingSnag,
  submitLabel = "Submit for Approval",
  onImageClick,
  attachments,
  loadingAttachments,
  uploadingAttachments,
  onUploadAttachments,
  onDeleteAttachment,
  isAdmin,
}) {
  const resolvedStateMessage =
    ASSIGNEE_RESOLVED_STATE_MESSAGE[snag.status] ||
    "No resolution uploaded yet";

  return (
    <div className="space-y-5 overflow-y-auto pr-1">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-sm-border bg-white p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-dark-red" />
            <h3 className="text-lg font-semibold text-primary-text">
              Original Issue
            </h3>
          </div>

          <div
            className="aspect-video cursor-zoom-in overflow-hidden rounded-lg border border-sm-border bg-gray-bg"
            onClick={() => onImageClick?.(snag.image, snag.code)}
          >
            <img
              src={snag.image || "/placeholder.svg"}
              alt="Original issue"
              className="h-full w-full object-cover"
            />
          </div>

          <p className="mt-4 text-base text-text-gray">{snag.description}</p>

          <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-text-gray sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              <span>
                {snag.category} {snag.subcategory ? `- ${snag.subcategory}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>By {snag.createdBy || "Unknown"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Created {snag.createdAt || "N/A"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Due {snag.expectedClosure || "N/A"}</span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Badge className={`${statusColors[snag.status] || ""} text-xs`}>
              {snag.status}
            </Badge>
          </div>
        </div>

        <div className="rounded-xl border border-sm-border bg-white p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-dark-green" />
            <h3 className="text-lg font-semibold text-primary-text">
              Resolved Submission
            </h3>
          </div>

          {canUpload ? (
            <div className="space-y-3">
              {resolvedImageUrl ? (
                <div
                  className="relative aspect-video cursor-zoom-in overflow-hidden rounded-lg border border-sm-border bg-gray-bg"
                  onClick={(e) => {
                    if (e.target.tagName !== "BUTTON") {
                      onImageClick?.(resolvedImageUrl, `${snag.code} - Resolved`);
                    }
                  }}
                >
                  <img
                    src={resolvedImageUrl}
                    alt="Resolved submission"
                    className="h-full w-full object-cover"
                  />
                  {typeof onClearImage === "function" && resolvedImageName ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearImage();
                      }}
                      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black/85"
                      aria-label="Remove uploaded image"
                      title="Remove uploaded image"
                    >
                      x
                    </button>
                  ) : null}
                </div>
              ) : null}

              {typeof onImageChange === "function" && (
                <>
                  <label
                    htmlFor={`resolved-image-${snag.id}`}
                    className={
                      resolvedImageUrl
                        ? "inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-sm-border bg-gray-bg/30 px-3 text-xs text-primary-text transition hover:border-primary/50"
                        : "flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-sm-border bg-gray-bg/40 text-center transition hover:border-primary/50"
                    }
                  >
                    {resolvedImageUrl ? (
                      <>
                        <Upload className="h-3.5 w-3.5 text-primary" />
                        <span>Upload another image</span>
                      </>
                    ) : (
                      <>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sm-primary/10">
                          <Upload className="h-5 w-5 text-primary" />
                        </div>
                        <p className="text-sm font-medium text-primary-text">
                          Drag & drop or click to upload
                        </p>
                        <p className="text-xs text-text-gray">
                          Upload resolved image for approval
                        </p>
                      </>
                    )}
                  </label>
                  <input
                    id={`resolved-image-${snag.id}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      onImageChange?.(file);
                      // Reset value so selecting the same file again triggers change.
                      e.target.value = "";
                    }}
                    className="hidden"
                  />
                </>
              )}

              {resolvedImageName ? (
                <p className="text-xs text-text-gray">
                  Selected: {resolvedImageName}
                </p>
              ) : null}

              <Textarea
                placeholder="Add remarks about the fix..."
                value={resolvedRemarks}
                onChange={(e) => setResolvedRemarks(e.target.value)}
                className="min-h-[90px] border-sm-border bg-background text-sm"
              />
              <Button
                className="h-10 w-full bg-sm-primary text-sm text-white hover:bg-sm-hover"
                onClick={onSubmitResolution}
                disabled={updatingSnag}
              >
                <CheckCircle2 className="h-4 w-4" />
                {updatingSnag ? "Updating..." : submitLabel}
              </Button>
            </div>
          ) : (
            <>
              {resolvedImageUrl ? (
                <div
                  className="aspect-video cursor-zoom-in overflow-hidden rounded-lg border border-sm-border bg-gray-bg"
                  onClick={() => onImageClick?.(resolvedImageUrl, `${snag.code} - Resolved`)}
                >
                  <img
                    src={resolvedImageUrl}
                    alt="Resolved submission"
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex aspect-video flex-col items-center justify-center gap-2 rounded-lg border border-sm-border bg-gray-bg/30">
                  <ImageIcon className="h-8 w-8 text-text-gray/60" />
                  <p className="text-sm text-text-gray">{resolvedStateMessage}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <AttachmentsCard
        attachments={attachments}
        loading={loadingAttachments}
        uploading={uploadingAttachments}
        onUpload={onUploadAttachments}
        onDelete={onDeleteAttachment}
        onImageClick={onImageClick}
        isAdmin={isAdmin}
      />
      <ActivityTimeline activities={snag.activities} onImageClick={onImageClick} />
    </div>
  );
}

/* ------------------ DETAILS ------------------ */

function IssueDetailsCard({ snag }) {
  const tags = Array.isArray(snag?.tags) ? snag.tags : [];

  return (
    <div className="rounded-xl border border-sm-border bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-gray">
        Issue Details
      </p>
      <p className="mt-3 text-base text-primary-text">{snag.description}</p>

      <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-text-gray sm:grid-cols-2">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4" />
          <span>
            {snag.category} {snag.subcategory ? `- ${snag.subcategory}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <User className="h-4 w-4" />
          <span>By {snag.createdBy || "Unknown"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>Created {snag.createdAt || "N/A"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>Due {snag.expectedClosure || "N/A"}</span>
        </div>
      </div>

      {Array.isArray(snag.assignedTo) && snag.assignedTo.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-gray">
            Assigned To
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {snag.assignedTo.map((name) => (
              <Badge
                key={name}
                className="border border-sm-border bg-gray-bg text-xs text-primary-text"
              >
                {name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-gray">
            Tags
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Badge
                key={tag}
                className="border border-sm-border bg-background text-xs text-text-gray"
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------ ATTACHMENTS ------------------ */

function AttachmentsCard({
  attachments = [],
  loading,
  uploading,
  onUpload,
  onDelete,
  onImageClick,
  isAdmin,
}) {
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onUpload(files);
      e.target.value = ""; // reset
    }
  };

  return (
    <div className="rounded-xl border border-sm-border bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-base font-medium text-primary-text"> Supported Documents</p>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input
              type="file"
              multiple
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-sm-border text-xs"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Upload
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-text-gray italic">Loading attachments...</p>
      ) : attachments.length ? (
        <div className="grid grid-cols-5 gap-3 sm:grid-cols-4">
          {attachments.map((a) => {
            const isImg = isImageFile(a.file);
            const fileName = a.file ? a.file.split("/").pop() : "File";

            return (
              <div key={a.id} className="group relative flex flex-col rounded-lg border border-sm-border bg-sm-card/10 p-2 transition hover:border-sm-primary/30">
                <div
                  className={`relative aspect-video overflow-hidden rounded bg-gray-bg/50 flex items-center justify-center ${isImg ? "cursor-zoom-in" : ""
                    }`}
                  onClick={() => isImg && onImageClick(a.file, fileName)}
                >
                  {isImg ? (
                    <img
                      src={a.file}
                      alt={fileName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-text-gray/50">
                      <File size={32} />
                      <span className="text-[10px] uppercase font-bold tracking-wider">
                        {fileName.split(".").pop()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex items-start justify-between gap-1">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium text-primary-text" title={fileName}>
                      {fileName}
                    </p>
                    <p className="text-[10px] text-text-gray">
                      By {a.uploaded_by || "User"} • {a.uploaded_on || "N/A"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <a
                      href={a.file}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded p-1 text-text-gray hover:bg-sm-primary/10 hover:text-sm-primary"
                      title="Download"
                    >
                      <Download size={14} />
                    </a>
                    {isAdmin && (
                      <button
                        onClick={() => onDelete(a.id)}
                        className="rounded p-1 text-text-gray hover:bg-red-500/10 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-text-gray">No attachments yet</p>
      )}
    </div>
  );
}

/* ------------------ TIMELINE ------------------ */

function ActivityTimeline({ activities = [], onImageClick }) {
  return (
    <div className="rounded-xl border border-sm-border bg-white p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-gray">
        Activity Timeline
      </p>
      <div className="space-y-0">
        {activities.map((a, index) => (
          <ActivityItem
            key={a.id}
            activity={a}
            isLast={index === activities.length - 1}
            onImageClick={onImageClick}
          />
        ))}
        {activities.length === 0 && (
          <p className="text-sm text-text-gray">No activity available.</p>
        )}
      </div>
    </div>
  );
}

function ActivityItem({ activity, isLast, onImageClick }) {
  const Icon = activityIconMap[activity.type] || PlusCircle;
  const initials = String(activity.user || "U")
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="z-10 mt-0.5 rounded-full bg-sm-primary/10 p-2">
          <Icon className="h-4 w-4 text-sm-primary" />
        </div>
        {!isLast && <div className="my-1 w-px flex-1 bg-sm-border" />}
      </div>
      <div className={cn("min-w-0 flex-1", !isLast && "pb-6")}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sm-primary/10 text-[10px] font-semibold text-sm-primary">
            {initials}
          </span>
          <p className="text-sm font-medium text-primary-text">{activity.user}</p>
          {activity.timestamp ? (
            <p className="text-xs text-text-gray">{activity.timestamp}</p>
          ) : null}
        </div>
        <p className="mt-0.5 text-sm text-text-gray">{activity.text}</p>
        {activity.attachment && (
          <div
            className="mt-2 h-20 w-32 cursor-zoom-in overflow-hidden rounded border border-sm-border bg-gray-bg/50"
            onClick={() => onImageClick?.(activity.attachment, `Activity by ${activity.user}`)}
          >
            <img
              src={activity.attachment}
              alt="Activity attachment"
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------ COMMENT ------------------ */

function CommentInput({ comment, setComment, onSubmit, submitting }) {
  return (
    <div className="rounded-xl border border-sm-border bg-white p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-gray">
        Add Comment
      </p>
      <div className="flex items-end gap-2">
        <Textarea
          placeholder="Write a comment or @mention someone..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onSubmit?.();
            }
          }}
          className="min-h-[64px] border-sm-border bg-background text-sm"
        />
        <Button
          className="h-9 w-9 bg-sm-primary p-0 text-white hover:bg-sm-hover"
          onClick={onSubmit}
          disabled={submitting || !String(comment || "").trim()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------ BEFORE AFTER ------------------ */

function BeforeAfterSection() {
  return (
    <div className="rounded-xl border border-sm-border bg-background p-4">
      <p className="text-base font-medium text-primary-text">Before / After</p>
      <p className="mt-1 text-sm text-text-gray">No data available</p>
    </div>
  );
}
